import * as yup from 'yup';
import { Link } from 'react-router-dom';
import { typeModalMapping } from '../../../../components/ExpenseClaims/AdditionalDetailsModal/modalConfig';
import { DateUtil } from '../../../../utils';
import { getCompanyCode } from '../../../../config/axios.config';

// helper to turn blank strings into null
function emptyStringToNull(value, originalValue) {
  return originalValue === '' ? null : value;
}

const baseExpenseClaimShape = {
  expCategory: yup.string().required('Expense Category is required'),
  expType: yup.string().required('Expense Type is required'),
  expDate: yup.string().required('Expense Date is required'),
  expRate: yup
    .number()
    .typeError('Exchange Rate is required')
    .required('Exchange Rate is required'),
  currency: yup.string().required('Currency is required'),
  /* amount: yup
    .number()
    .typeError('Expense Amount must be a number')
    .required('Expense Amount is required'), */
  amount: yup
    .number()
    .transform((value, originalValue) => {
      // Remove commas before converting
      const numeric = originalValue
        ? originalValue.toString().replace(/,/g, '')
        : '';
      return numeric === '' ? NaN : Number(numeric);
    })
    .typeError('Expense Amount must be a number')
    .required('Expense Amount is required'),
  // .positive('Expense Amount must be greater than 0'),
  taxAmount: yup
    .number()
    .nullable()
    .transform(emptyStringToNull)
    .typeError('Tax Amount must be a number'),
  remarks: yup.string().notRequired(),
};

const modalFieldsShape = {
  reimburAmt: yup.number(),
  sorNo: yup.string(),
  entity: yup.string(),
  countryCode: yup.string(),
  dependent: yup.number(),
  expFromDate: yup.string(),
  expToDate: yup.string(),
  expCostAlloc: yup.string(),
  expCustomer: yup.string(),
  product: yup.string(),
};

// Build conditional modal fields schema based only on expType
const conditionalShape = Object.entries(modalFieldsShape).reduce(
  (acc, [field, schemaType]) => {
    acc[field] = schemaType.when('expType', (expType, schema) => {
      const config = typeModalMapping[expType] || {};
      const fieldConfig = config[field];

      // If there's a mapping for this field under the selected expType
      if (fieldConfig) {
        let s = schema;

        // Required handling
        if (fieldConfig.validation?.required?.value) {
          s = s.required(fieldConfig.validation.required.message);
          if (fieldConfig.datatype === 'number') {
            // Ensure that number is not -1
            s = s.test(
              'not-default',
              fieldConfig.validation.required.message,
              value => value !== -1,
            );
          }
        }

        // Regex pattern handling (if provided)
        if (fieldConfig.validation?.pattern) {
          s = s.matches(
            fieldConfig.validation.pattern.value,
            fieldConfig.validation.pattern.message,
          );
        }

        return s;
      }

      // Otherwise, leave as-is
      return schema;
    });

    return acc;
  },
  {},
);

// 6) Final row schema: merge base + all modal fields
export const rowSchema = yup.object().shape({
  ...baseExpenseClaimShape,
  ...conditionalShape,
});

export const validationSchema = yup.object({
  claims: yup
    .array()
    .transform((_, originalValue) => {
      if (!Array.isArray(originalValue)) {
        return originalValue;
      }
      return originalValue.length > 1
        ? originalValue.slice(0, -1)
        : originalValue;
    })
    .of(rowSchema)
    .min(1, 'At least one row is required'),
});

export const modalFieldInitialValues = {
  sorNo: '',
  entity: getCompanyCode(),
  countryCode: '',
  dependent: -1,
  expFromDate: '',
  expToDate: '',
  expCostAlloc: 'C00000',
  expCustomer: '',
  product: '',
};

export const initialValues = {
  expCategory: '',
  expType: '',
  expDate: DateUtil.formatDateObjToHtmlDate(new Date()),
  expRate: '',
  currency: '',
  taxAmount: null,
  amount: null,
  remarks: '',
  ...modalFieldInitialValues,
};

export const defaultValues = {
  claims: [initialValues],
};

// Draft Data config
export function draftDataConfig(config) {
  return [
    {
      label: 'Claim No',
      key: 'ClaimNo',
      render: item => (
        <Link
          to={`/expense/claim?ID=${item?.ClaimNo}`}
          onClick={() => {
            // if it's the same ID as before, force a page reload
            if (
              new URLSearchParams(window.location.search).get('ID') ===
              item.ClaimNo
            ) {
              window.location.reload();
            }
            config?.onClose?.();
          }}
        >
          {item?.ClaimNo}
        </Link>
      ),
    },
    {
      label: 'Claim Date',
      key: 'ClaimDt',
      render: item =>
        new Date(item?.ClaimDt).toLocaleDateString('en-GB', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }),
    },
    {
      label: 'Amount',
      key: 'Amt',
      render: item =>
        Number(item?.Amt || 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    { label: 'Claim Status', key: 'ClaimStatus' },
  ];
}

export const CLAIM_FORM_TYPES = {
  CREATE: 'create',
  VIEW: 'view',
};

export const CLAIM_STATUS_COLOR = {
  Approved: '#aaf7c1',
  'Pending for Approval': '#FEF0C7',
};

export function getClaimStatusColor(status) {
  return CLAIM_STATUS_COLOR[status] ?? '#FEF3F2';
}

export function getClaimApprovalColor(status) {
  return status === 'Pending for Approval' ? '#000' : '#000';
}

// Submission error Data config
export const submitDataConfig = () => [
  {
    label: 'Expense Category',
    key: 'Expense Category',
  },
  {
    label: 'Expense Type',
    key: 'Expense Type',
  },
  {
    label: 'Currency',
    key: 'Exp.Curr',
  },
  {
    label: 'Amount',
    key: 'Amount',
  },
  {
    label: 'Error Message',
    key: 'Error Message',
    colStyle: { width: '300px' },
  },
];
